"use client";

import React, { useState, useEffect, useRef } from "react";
import StarRating from "../components/StarRating";
import ToggleSwitch from "../components/ToggleSwitch";
import YooLogo from "../components/YooLogo";
import BillingSummary from "../components/BillingSummary";
import { Link, useNavigate } from "react-router-dom";

// SimliE2E SDK constants - default values
const DEFAULT_SIMLI_FACE_ID = "137aa749-6bcd-44d4-989f-a6c666fdf871";
const DEFAULT_SIMLI_VOICE_ID = "8bc1c0f2-3013-4b96-8acc-8182fa71fa86";

// Add this function to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Add this function to get user ID consistently
const getUserId = (user) => {
  if (!user) return null;
  // Return _id if it exists, otherwise id
  return user._id || user.id || null;
};

// Add this function to track joke views on the server
const trackJokeView = async (userId, jokeId) => {
  try {
    if (!userId || !jokeId) {
      console.error("Cannot track joke view: missing userId or jokeId");
      return null;
    }

    console.log("Tracking joke view:", { userId, jokeId });
    const response = await fetch("http://localhost:5001/api/jokes/track-view", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
      body: JSON.stringify({ userId, jokeId, timestamp: Date.now() }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Joke view tracking error:", errorData);
      throw new Error(errorData.error || "Failed to track joke view");
    }

    const data = await response.json();
    console.log("Joke view tracked, count:", data.viewsCount);
    return data.viewsCount;
  } catch (error) {
    console.error("Failed to track joke view:", error);
    return null;
  }
};

// Add this function to get the user's joke view count
const getUserJokeViews = async (userId) => {
  try {
    const response = await fetch(
      `http://localhost:5001/api/users/${userId}/joke-views`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to get joke views");
    }

    const data = await response.json();
    return data.viewsCount;
  } catch (error) {
    console.error("Failed to get joke views:", error);
    return null;
  }
};

// Add this function to deduct user credits - renamed from useUserCredit to deductUserCredit
const deductUserCredit = async (userId) => {
  try {
    console.log("Calling credit usage API for user:", userId);
    const response = await fetch(
      `http://localhost:5001/api/users/${userId}/use-credit`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        // Add a timestamp to avoid caching issues
        body: JSON.stringify({ timestamp: Date.now() }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Credit API error:", errorData);
      throw new Error(errorData.error || "Failed to use credit");
    }

    const data = await response.json();
    console.log(
      "Credit usage successful, remaining credits:",
      data.creditsRemaining
    );
    return data.creditsRemaining;
  } catch (error) {
    console.error("Failed to use credit:", error);
    return null;
  }
};

// Add this function to refresh user data from the server
const refreshUserData = async (userId) => {
  try {
    if (!userId) {
      console.error("Cannot refresh user data: userId is undefined");
      return null;
    }

    console.log("Refreshing user data for user:", userId);
    const response = await fetch(
      `http://localhost:5001/api/users/${userId}/credits`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("User data refresh error:", errorData);
      throw new Error(errorData.error || "Failed to fetch user credits");
    }

    const data = await response.json();
    console.log("User credits refreshed:", data.credits);
    return data.credits;
  } catch (error) {
    console.error("Failed to refresh user data:", error);
    return null;
  }
};

export default function JokeDetailsPage() {
  const navigate = useNavigate();

  // Get faceId and voiceId from localStorage or use defaults
  const [faceId, setFaceId] = useState(() => {
    return localStorage.getItem("faceId") || DEFAULT_SIMLI_FACE_ID;
  });

  const [voiceId, setVoiceId] = useState(() => {
    return localStorage.getItem("voiceId") || DEFAULT_SIMLI_VOICE_ID;
  });

  // Store faceId and voiceId in localStorage if they change
  useEffect(() => {
    if (faceId) {
      localStorage.setItem("faceId", faceId);
    }
    if (voiceId) {
      localStorage.setItem("voiceId", voiceId);
    }
  }, [faceId, voiceId]);

  // Re-initialize SimliE2E when faceId or voiceId changes
  useEffect(() => {
    if (simliInstance.current) {
      simliInstance.current.destroy();
      setTimeout(() => {
        initializeSimli();
      }, 100);
    }
  }, [faceId, voiceId]);

  const [jokeDetails, setJokeDetails] = useState({
    title: "",
    category: "",
    familyFriendly: true,
    is18Plus: false,
    stars: 0,
    punchline: false,
    topRanked: false,
    jokesList: [],
    allJokesList: [], // Store all jokes
    filteredJokesList: [], // Store filtered jokes
    punchlineText: "",
  });

  const [activeTab, setActiveTab] = useState("random");
  const [selectedJoke, setSelectedJoke] = useState({});
  const [currentJokeIndex, setCurrentJokeIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPunchline, setShowPunchline] = useState(false);
  const [avgStars, setAvgStars] = useState(0);
  const [starFilter, setStarFilter] = useState(1); // Default to 1 star filter
  const [showTopRankedOnly, setShowTopRankedOnly] = useState(false); // Filter by top ranked

  // SimliE2E related states
  const [simliReady, setSimliReady] = useState(false);
  const [isSimliPlaying, setIsSimliPlaying] = useState(false);
  const [isSimliLoading, setIsSimliLoading] = useState(false);
  const [roomUrl, setRoomUrl] = useState(null);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const simliContainerRef = useRef(null);
  const simliInstance = useRef(null);

  // Modify the joke view tracking to use server-side if user is logged in
  const [jokesViewed, setJokesViewed] = useState(() => {
    return parseInt(localStorage.getItem("jokesViewed") || "0");
  });
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const MAX_FREE_JOKES = 5;

  // Get user from token instead of localStorage
  const [user, setUser] = useState(null);

  // Effect to fetch user data using getAuthHeader
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const response = await fetch("http://localhost:5001/api/auth/me", {
            headers: {
              ...getAuthHeader(),
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            // Token invalid, clear it
            localStorage.removeItem("token");
          }
        }
      } catch (error) {
        console.error("Failed to fetch user:", error);
      }
    };

    fetchUser();
  }, []);

  // Check server-side joke views when component mounts
  useEffect(() => {
    const checkServerJokeViews = async () => {
      if (user?.id) {
        const serverViewCount = await getUserJokeViews(user.id);
        if (serverViewCount !== null) {
          // Use the higher count between server and local storage
          const localCount = parseInt(
            localStorage.getItem("jokesViewed") || "0"
          );
          const finalCount = Math.max(serverViewCount, localCount);

          setJokesViewed(finalCount);
          localStorage.setItem("jokesViewed", finalCount.toString());
        }
      }
    };

    checkServerJokeViews();
  }, [user]);

  // Initialize SimliE2E
  useEffect(() => {
    // Check if SimliE2E SDK is available
    if (window.SimliE2E) {
      initializeSimli();
    } else {
      // Load SimliE2E SDK script if not available
      const script = document.createElement("script");
      script.src = "https://sdk.simli.ai/latest/simli-e2e.min.js";
      script.async = true;
      script.onload = initializeSimli;
      document.body.appendChild(script);
    }

    return () => {
      // Cleanup SimliE2E instance on component unmount
      if (simliInstance.current) {
        simliInstance.current.destroy();
      }
    };
  }, []);

  // Initialize SimliE2E instance
  const initializeSimli = async () => {
    try {
      if (simliContainerRef.current && window.SimliE2E) {
        setIsSimliLoading(true);

        // Create SimliE2E instance
        simliInstance.current = new window.SimliE2E({
          container: simliContainerRef.current,
          faceId: faceId,
          voiceId: voiceId,
          initialMood: "happy",
          autoplay: false,
        });

        // Initialize instance
        await simliInstance.current.initialize();
        setSimliReady(true);
        setIsSimliLoading(false);
      }
    } catch (err) {
      console.error("Failed to initialize SimliE2E:", err);
      setError("Failed to load avatar. Please try again later.");
      setIsSimliLoading(false);
    }
  };

  // Reset IDs to defaults
  const resetToDefaultIds = () => {
    setFaceId(DEFAULT_SIMLI_FACE_ID);
    setVoiceId(DEFAULT_SIMLI_VOICE_ID);
    // Re-initialize with default IDs
    if (simliInstance.current) {
      simliInstance.current.destroy();
      setTimeout(() => {
        initializeSimli();
      }, 100);
    }
  };

  // Play joke on SimliE2E
  const playJokeOnSimli = async () => {
    if (!simliReady || !simliInstance.current) return;

    try {
      setIsSimliPlaying(true);

      // Get current joke text
      const currentJoke = jokeDetails.jokesList[currentJokeIndex];
      let jokeText = currentJoke.title || currentJoke.body || "";

      // Add punchline if available and shown
      if (showPunchline && jokeDetails.punchlineText) {
        jokeText += " " + jokeDetails.punchlineText;
      }

      if (jokeText) {
        // Play the joke text using SimliE2E
        await simliInstance.current.speak(jokeText);
      }

      setIsSimliPlaying(false);
    } catch (err) {
      console.error("Failed to play joke on SimliE2E:", err);
      setIsSimliPlaying(false);
    }
  };

  // Fetch jokes from backend
  useEffect(() => {
    const fetchJokes = async () => {
      try {
        setLoading(true);
        const response = await fetch("http://localhost:5001/api/jokes");
        if (!response.ok) {
          throw new Error("Failed to fetch jokes");
        }
        const jokes = await response.json();

        // Ensure jokes have default values for important fields
        const processedJokes = jokes.map((joke) => ({
          ...joke,
          stars: joke.stars || 0,
          topRanked: joke.topRanked || false,
          punchline: joke.punchline || "",
        }));

        // Calculate average stars for all jokes
        const validRatings = processedJokes.filter((joke) => joke.stars > 0);
        const avgStarsValue =
          validRatings.length > 0
            ? validRatings.reduce((sum, joke) => sum + joke.stars, 0) /
              validRatings.length
            : 0;
        setAvgStars(avgStarsValue.toFixed(1));

        // Filter jokes for initial view (default: family-friendly)
        const kidsJokes = processedJokes.filter((joke) => joke.type === "kids");

        setJokeDetails((prev) => ({
          ...prev,
          jokesList: kidsJokes.length > 0 ? kidsJokes : processedJokes,
          allJokesList: processedJokes,
          filteredJokesList: kidsJokes,
        }));

        if (processedJokes.length > 0) {
          setCurrentJokeIndex(0);
          const initialJoke =
            kidsJokes.length > 0 ? kidsJokes[0] : processedJokes[0];
          setSelectedJoke(initialJoke);

          // Initialize punchline toggle based on whether joke has a punchline
          setShowPunchline(
            initialJoke.punchline && initialJoke.punchline.trim() !== ""
          );
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchJokes();
  }, []);

  useEffect(() => {
    if (jokeDetails.jokesList[currentJokeIndex]) {
      const currentJoke = jokeDetails.jokesList[currentJokeIndex];
      setSelectedJoke(currentJoke);
      setJokeDetails((prev) => ({
        ...prev,
        title: currentJoke.title || "",
        category: currentJoke.type || "",
        familyFriendly: currentJoke.type === "kids",
        is18Plus: currentJoke.type === "adult",
        stars: currentJoke.stars || 0,
        topRanked: currentJoke.topRanked || false,
        punchlineText: currentJoke.punchline || "",
      }));

      // Auto-show punchline if it exists, otherwise hide it
      setShowPunchline(
        currentJoke.punchline && currentJoke.punchline.trim() !== ""
      );
    }
  }, [currentJokeIndex, jokeDetails.jokesList]);

  // Handle star rating as a filter
  const handleStarFilterChange = (rating) => {
    setStarFilter(rating);
    // Apply filters immediately
    applyFilters(rating, showTopRankedOnly);
  };

  const handleTogglePunchline = (value) => {
    setShowPunchline(value);
  };

  // Handle top ranked toggle as a filter
  const handleToggleTopRankedFilter = (value) => {
    setShowTopRankedOnly(value);
    // Apply filters immediately
    applyFilters(starFilter, value);
  };

  // Apply all current filters with optional parameters to use updated values
  const applyFilters = (
    starValue = starFilter,
    topRankedValue = showTopRankedOnly
  ) => {
    // Start with the original filter based on joke type (kids/adult)
    let filtered = jokeDetails.allJokesList.filter((joke) =>
      jokeDetails.familyFriendly ? joke.type === "kids" : joke.type === "adult"
    );

    // Apply star filter if set
    if (starValue > 0) {
      // For 1 star, include jokes with 0 or 1 stars
      if (starValue === 1) {
        filtered = filtered.filter((joke) => joke.stars <= 1);
      } else {
        // For 2+ stars, filter as normal
        filtered = filtered.filter((joke) => joke.stars >= starValue);
      }
    }

    // Apply top ranked filter if enabled
    if (topRankedValue) {
      filtered = filtered.filter((joke) => joke.topRanked);
    }

    setJokeDetails((prev) => ({
      ...prev,
      jokesList: filtered.length > 0 ? filtered : [],
      filteredJokesList: filtered,
    }));

    // Reset to first joke of filtered list
    if (filtered.length > 0) {
      setCurrentJokeIndex(0);
      setSelectedJoke(filtered[0]);
    }
  };

  const handleSaveJoke = async () => {
    try {
      const currentJoke = jokeDetails.jokesList[currentJokeIndex];

      if (!currentJoke || (!currentJoke._id && !currentJoke.id)) {
        setError("Cannot save joke: invalid joke data");
        return;
      }

      // Use _id if available, otherwise use id
      const jokeId = currentJoke._id || currentJoke.id;

      // Create an object with only the fields we want to update
      const updateData = {
        stars: jokeDetails.stars,
        topRanked: jokeDetails.topRanked,
      };

      const response = await fetch(
        `http://localhost:5001/api/jokes/${jokeId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader(),
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to save joke");
      }

      const updatedJoke = await response.json();

      // Update the joke in our lists
      setJokeDetails((prev) => {
        // Create updated lists with the modified joke
        const updateJokeInList = (list) =>
          list.map((joke) => {
            if (
              (joke._id && joke._id === jokeId) ||
              (joke.id && joke.id === jokeId)
            ) {
              return { ...joke, ...updateData };
            }
            return joke;
          });

        return {
          ...prev,
          jokesList: updateJokeInList(prev.jokesList),
          allJokesList: updateJokeInList(prev.allJokesList),
          filteredJokesList: updateJokeInList(prev.filteredJokesList),
        };
      });

      // Show success message
      alert("Joke saved successfully!");
    } catch (err) {
      setError(err.message || "Failed to save joke");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === "saved") {
      // If switching to saved tab, filter to show only top-ranked jokes
      const savedJokes = jokeDetails.allJokesList.filter(
        (joke) => joke.topRanked
      );
      setJokeDetails((prev) => ({
        ...prev,
        jokesList: savedJokes.length > 0 ? savedJokes : [],
      }));

      if (savedJokes.length > 0) {
        setCurrentJokeIndex(0);
        setSelectedJoke(savedJokes[0]);
      }
    } else {
      // Reset filters when switching back to random tab
      setStarFilter(1);
      setShowTopRankedOnly(false);

      // Return to the appropriate filtered list based on family-friendly setting
      const filtered = jokeDetails.allJokesList.filter((joke) =>
        jokeDetails.familyFriendly
          ? joke.type === "kids"
          : joke.type === "adult"
      );

      // Apply the default star filter (1 star includes 0-1 stars)
      const finalFiltered = filtered.filter((joke) => joke.stars <= 1);

      setJokeDetails((prev) => ({
        ...prev,
        jokesList: finalFiltered.length > 0 ? finalFiltered : [],
        filteredJokesList: finalFiltered,
      }));

      if (finalFiltered.length > 0) {
        setCurrentJokeIndex(0);
        setSelectedJoke(finalFiltered[0]);
      }
    }
  };

  // Add new state for remaining jokes
  const [remainingJokes, setRemainingJokes] = useState(MAX_FREE_JOKES);

  // Update effect to calculate remaining jokes
  useEffect(() => {
    // Calculate remaining free jokes or credits
    const calculateRemainingJokes = () => {
      if (user?.credits) {
        // For premium users, their remaining jokes are their credits
        setRemainingJokes(user.credits);
      } else {
        // For free users, calculate remaining free jokes
        const remaining = Math.max(0, MAX_FREE_JOKES - jokesViewed);
        setRemainingJokes(remaining);
      }
    };

    calculateRemainingJokes();
  }, [jokesViewed, user]);

  // Add this effect to identify the user ID issue
  useEffect(() => {
    if (user) {
      console.log("Current user object:", user);
      // Check if we have a valid ID
      const userId = getUserId(user);
      if (!userId) {
        console.warn("User object lacks a proper ID:", user);
      } else {
        console.log("Using user ID:", userId);
      }
    }
  }, [user]);

  // Update the useEffect for refreshing user credits
  useEffect(() => {
    const updateUserCredits = async () => {
      if (user) {
        // Use _id or id, depending on what's available
        const userId = getUserId(user);
        if (!userId) {
          console.error("Cannot refresh user data: User has no valid ID");
          return;
        }

        const credits = await refreshUserData(userId);
        if (credits !== null) {
          // Update local user data with fresh credits
          const updatedUser = { ...user, credits };
          setUser(updatedUser);
        }
      }
    };

    updateUserCredits();
  }, []); // Only run once on component mount

  // Update handleJokeSelect to handle credit usage
  const handleJokeSelect = async (joke, index) => {
    // Skip if same joke
    if (currentJokeIndex === index) {
      return;
    }

    // Check if user has reached the joke limit
    if (!user?.credits && jokesViewed >= MAX_FREE_JOKES) {
      setShowPremiumModal(true);
      return;
    }

    // If user has credits, deduct one
    if (user && user.credits > 0) {
      // Get the correct user ID
      const userId = getUserId(user);
      if (!userId) {
        console.error("Cannot use credit: User has no valid ID");
        return;
      }

      // Deduct a credit and update user's remaining credits
      const creditsRemaining = await deductUserCredit(userId);
      if (creditsRemaining !== null) {
        // Update local user data
        const updatedUser = { ...user, credits: creditsRemaining };
        setUser(updatedUser);
        setRemainingJokes(creditsRemaining);
      }
    } else {
      // Only track if it's a different joke
      // Track on server if user is logged in
      if (user) {
        const userId = getUserId(user);
        if (!userId) {
          console.error("Cannot track joke view: User has no valid ID");
          // Fallback to local tracking
          const newJokesViewed = jokesViewed + 1;
          setJokesViewed(newJokesViewed);
          localStorage.setItem("jokesViewed", newJokesViewed.toString());

          // Update remaining free jokes
          const remaining = Math.max(0, MAX_FREE_JOKES - newJokesViewed);
          setRemainingJokes(remaining);
          setCurrentJokeIndex(index);
          return;
        }

        const serverViewCount = await trackJokeView(userId, joke._id);
        if (serverViewCount !== null) {
          setJokesViewed(serverViewCount);
          localStorage.setItem("jokesViewed", serverViewCount.toString());

          // Update remaining free jokes
          const remaining = Math.max(0, MAX_FREE_JOKES - serverViewCount);
          setRemainingJokes(remaining);
        } else {
          // Fallback to local tracking if server fails
          const newJokesViewed = jokesViewed + 1;
          setJokesViewed(newJokesViewed);
          localStorage.setItem("jokesViewed", newJokesViewed.toString());

          // Update remaining free jokes
          const remaining = Math.max(0, MAX_FREE_JOKES - newJokesViewed);
          setRemainingJokes(remaining);
        }
      } else {
        // Local tracking if no user
        const newJokesViewed = jokesViewed + 1;
        setJokesViewed(newJokesViewed);
        localStorage.setItem("jokesViewed", newJokesViewed.toString());

        // Update remaining free jokes
        const remaining = Math.max(0, MAX_FREE_JOKES - newJokesViewed);
        setRemainingJokes(remaining);
      }
    }

    setCurrentJokeIndex(index);
  };

  // Update handleNextJoke to handle credit usage
  const handleNextJoke = async () => {
    if (currentJokeIndex < jokeDetails.jokesList.length - 1) {
      // Check if user has reached the joke limit
      if (!user?.credits && jokesViewed >= MAX_FREE_JOKES) {
        setShowPremiumModal(true);
        return;
      }

      const newIndex = currentJokeIndex + 1;
      const nextJoke = jokeDetails.jokesList[newIndex];

      // If user has credits, deduct one
      if (user && user.credits > 0) {
        // Get the correct user ID
        const userId = getUserId(user);
        if (!userId) {
          console.error("Cannot use credit: User has no valid ID");
          return;
        }

        // Deduct a credit and update user's remaining credits
        const creditsRemaining = await deductUserCredit(userId);
        if (creditsRemaining !== null) {
          // Update local user data
          const updatedUser = { ...user, credits: creditsRemaining };
          setUser(updatedUser);
          setRemainingJokes(creditsRemaining);
        }
      } else {
        // Track on server if user is logged in
        if (user) {
          const userId = getUserId(user);
          if (!userId) {
            console.error("Cannot track joke view: User has no valid ID");
            // Fallback to local tracking
            const newJokesViewed = jokesViewed + 1;
            setJokesViewed(newJokesViewed);
            localStorage.setItem("jokesViewed", newJokesViewed.toString());

            // Update remaining free jokes
            const remaining = Math.max(0, MAX_FREE_JOKES - newJokesViewed);
            setRemainingJokes(remaining);

            // Continue with setting current joke index
            setCurrentJokeIndex(newIndex);

            // Start avatar session with the next joke if available
            if (nextJoke) {
              setTimeout(() => {
                startAvatarSessionWithJoke(nextJoke);
              }, 100);
            }

            return;
          }

          const serverViewCount = await trackJokeView(userId, nextJoke._id);
          if (serverViewCount !== null) {
            setJokesViewed(serverViewCount);
            localStorage.setItem("jokesViewed", serverViewCount.toString());

            // Update remaining free jokes
            const remaining = Math.max(0, MAX_FREE_JOKES - serverViewCount);
            setRemainingJokes(remaining);
          } else {
            // Fallback to local tracking if server fails
            const newJokesViewed = jokesViewed + 1;
            setJokesViewed(newJokesViewed);
            localStorage.setItem("jokesViewed", newJokesViewed.toString());

            // Update remaining free jokes
            const remaining = Math.max(0, MAX_FREE_JOKES - newJokesViewed);
            setRemainingJokes(remaining);
          }
        } else {
          // Local tracking if no user
          const newJokesViewed = jokesViewed + 1;
          setJokesViewed(newJokesViewed);
          localStorage.setItem("jokesViewed", newJokesViewed.toString());

          // Update remaining free jokes
          const remaining = Math.max(0, MAX_FREE_JOKES - newJokesViewed);
          setRemainingJokes(remaining);
        }
      }

      // First set the current joke index
      setCurrentJokeIndex(newIndex);

      // Directly start the avatar session with the next joke
      if (nextJoke) {
        // Use a short delay to allow UI to update
        setTimeout(() => {
          startAvatarSessionWithJoke(nextJoke);
        }, 100);
      }
    }
  };

  const handlePreviousJoke = () => {
    if (currentJokeIndex > 0) {
      const newIndex = currentJokeIndex - 1;
      const prevJoke = jokeDetails.jokesList[newIndex];

      // First set the current joke index
      setCurrentJokeIndex(newIndex);

      // Directly start the avatar session with the previous joke
      if (prevJoke) {
        // Use a short delay to allow UI to update
        setTimeout(() => {
          startAvatarSessionWithJoke(prevJoke);
        }, 100);
      }
    }
  };

  // Auto-play joke when changing jokes if simli is ready
  useEffect(() => {
    if (simliReady && jokeDetails.jokesList.length > 0) {
      // Small delay to ensure the joke details are updated
      const timer = setTimeout(() => {
        playJokeOnSimli();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentJokeIndex, simliReady]);

  // Clear error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle emoji click to filter jokes
  const handleJokeTypeFilter = (isFamily) => {
    // Update family friendly state first
    const updatedFamily = isFamily;
    const updatedAdult = !isFamily;

    // Start with filtering based on the new joke type
    let filtered = jokeDetails.allJokesList.filter((joke) =>
      updatedFamily ? joke.type === "kids" : joke.type === "adult"
    );

    // Apply existing star filter
    if (starFilter > 0) {
      // For 1 star, include jokes with 0 or 1 stars
      if (starFilter === 1) {
        filtered = filtered.filter((joke) => joke.stars <= 1);
      } else {
        // For 2+ stars, filter as normal
        filtered = filtered.filter((joke) => joke.stars >= starFilter);
      }
    }

    // Apply top ranked filter if enabled
    if (showTopRankedOnly) {
      filtered = filtered.filter((joke) => joke.topRanked);
    }

    setJokeDetails((prev) => ({
      ...prev,
      familyFriendly: updatedFamily,
      is18Plus: updatedAdult,
      jokesList: filtered.length > 0 ? filtered : [],
      filteredJokesList: filtered,
    }));

    // Reset to first joke of filtered list
    if (filtered.length > 0) {
      setCurrentJokeIndex(0);
      setSelectedJoke(filtered[0]);
    }
  };

  // Start E2E avatar session with a specific joke
  const startAvatarSessionWithJoke = async (joke) => {
    if (!faceId || !voiceId || !joke) {
      setError("Face ID and Voice ID are required or joke is missing");
      return;
    }

    try {
      setIsStartingSession(true);

      // Format joke text properly
      let jokeText = joke.body || "";

      // Add the joke body if it exists and is different from title
      // if (joke.body && joke.body !== joke.title) {
      //   jokeText += " " + joke.body;
      // }

      // Add punchline if available and showing punchlines is enabled
      // if (showPunchline && joke.punchline) {
      //   jokeText += " " + joke.punchline;
      // }

      // Ensure we have text to speak
      if (!jokeText.trim()) {
        throw new Error("No joke text available");
      }

      console.log("Starting avatar session with joke:", jokeText);

      const payload = {
        apiKey: "n8sesdejo1tnudor5ljcg",
        faceId: faceId,
        voiceId: voiceId,
        ttsProvider: "Cartesia",
        ttsModel: "sonic-turbo-2025-03-07",
        ttsAPIKey: "sk_car_ANhQon9hRbqMDBgR5Z5n9Q",
        systemPrompt: joke.punchline
          ? `You are a helpful assistant that tells jokes. Tell this joke exactly as written, word for word. after the responds with the following punchline, say "That's the joke!": ${joke.punchlineText}`
          : "You are a helpful assistant that tells jokes. Tell this joke exactly as written, word for word. ",
        firstMessage: jokeText,
        language: "en",
        maxSessionLength: 3600,
        maxIdleTime: 300,
      };

      const res = await fetch("https://api.simli.ai/startE2ESession", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Session start failed: ${res.status} - ${errText}`);
      }

      const result = await res.json();

      if (result?.roomUrl) {
        setRoomUrl(result.roomUrl);
      } else {
        throw new Error("No room URL returned from session creation");
      }
    } catch (err) {
      setError("Failed to start avatar session: " + err.message);
      console.error("Session error:", err);
    } finally {
      setIsStartingSession(false);
    }
  };

  // Original function now just calls the new function with current joke
  const startAvatarSession = async () => {
    const currentJoke = jokeDetails.jokesList[currentJokeIndex];
    if (currentJoke) {
      await startAvatarSessionWithJoke(currentJoke);
    } else {
      setError("No joke selected");
    }
  };

  // Add redirect function to subscription page
  const goToSubscriptionPage = () => {
    navigate("/subscribe");
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-red-500 text-2xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white">
      {/* Header with Logo */}
      <header className="w-full bg-white p-2 border-b border-gray-200 flex justify-between items-center relative">
        <YooLogo size="lg" />

        {/* Remaining Jokes Bubble */}
        <div className="absolute left-1/2 transform -translate-x-1/2 top-2">
          <div
            className={`rounded-full px-4 py-1.5 flex items-center justify-center shadow-md ${
              user?.credits
                ? user.credits <= 3
                  ? "bg-yellow-500"
                  : "bg-green-500"
                : remainingJokes <= 1
                ? "bg-red-500"
                : remainingJokes <= 2
                ? "bg-yellow-500"
                : "bg-blue-500"
            } text-white`}
          >
            <span className="font-medium mr-1.5">
              {user?.credits ? "Credits:" : "Free jokes:"}
            </span>
            <span className="font-bold text-lg">{remainingJokes}</span>
            {(user?.credits && user.credits <= 3) ||
            (!user?.credits && remainingJokes <= 2) ? (
              <span className="ml-2 animate-pulse">⚠️</span>
            ) : null}
          </div>

          {/* Low Credit Warning */}
          {((user?.credits && user.credits <= 3) ||
            (!user?.credits && remainingJokes <= 2 && remainingJokes > 0)) && (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-white border border-gray-300 rounded-md p-2 shadow-md text-xs z-10 w-48 text-center">
              <p className="font-medium text-gray-700">
                {user?.credits
                  ? `You have only ${user.credits} credit${
                      user.credits === 1 ? "" : "s"
                    } left!`
                  : `Only ${remainingJokes} free joke${
                      remainingJokes === 1 ? "" : "s"
                    } remaining!`}
              </p>
              <button
                onClick={goToSubscriptionPage}
                className="mt-1 bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 transition-colors"
              >
                Get More
              </button>
            </div>
          )}
        </div>

        <div className="flex space-x-4">
          <button
            className="p-1 rounded-full bg-white hover:bg-gray-100 transition-colors"
            onClick={() => {
              const newFaceId = prompt("Enter your Face ID:", faceId);
              if (newFaceId && newFaceId.trim() !== "") {
                setFaceId(newFaceId.trim());
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </button>
          <button
            className="p-1 rounded-full bg-white hover:bg-gray-100 transition-colors"
            onClick={() => {
              const newVoiceId = prompt("Enter your Voice ID:", voiceId);
              if (newVoiceId && newVoiceId.trim() !== "") {
                setVoiceId(newVoiceId.trim());
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          </button>
          <button
            className="p-1 rounded-full bg-white hover:bg-gray-100 transition-colors"
            onClick={resetToDefaultIds}
            title="Reset to default Face and Voice IDs"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          <button className="p-1 rounded-full bg-white hover:bg-gray-100 transition-colors">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel with joke details */}
        <div className="w-1/3 p-3 bg-white overflow-y-auto">
          <div className="bg-white rounded-lg shadow-md p-3">
            <h2 className="text-blue-600 text-lg mb-1">Title</h2>
            <p className="font-medium mb-3 text-black">{jokeDetails.title}</p>
            <h2 className="text-blue-600 text-lg mb-1">Category</h2>
            <div className="mb-3 flex items-center">
              <div
                className={`flex items-center justify-center bg-blue-100 rounded-full p-1 mr-2 cursor-pointer hover:bg-blue-200 transition-colors ${
                  jokeDetails.familyFriendly ? "ring-2 ring-blue-500" : ""
                }`}
                onClick={() => handleJokeTypeFilter(true)}
              >
                <span className="text-blue-500 text-xl">👶</span>
              </div>
              <div
                className={`flex items-center justify-center bg-blue-100 rounded-full p-1 mr-2 cursor-pointer hover:bg-blue-200 transition-colors ${
                  !jokeDetails.familyFriendly ? "ring-2 ring-blue-500" : ""
                }`}
                onClick={() => handleJokeTypeFilter(false)}
              >
                <span className="text-blue-500 text-xl">🔞</span>
              </div>
              {jokeDetails.is18Plus && (
                <div className="flex items-center justify-center bg-gray-300 rounded-full p-1 w-8 h-8 ml-2">
                  <span className="text-sm font-bold">18+</span>
                </div>
              )}
            </div>

            {/* Stars section - as a filter */}
            <div className="mb-3 bg-gray-50 p-2 rounded-lg">
              <h2 className="text-blue-600 text-lg mb-1">Filter by Stars</h2>
              <div className="flex items-center mb-1">
                <div className="mr-2">
                  <StarRating
                    initialRating={starFilter}
                    onChange={handleStarFilterChange}
                    size="md"
                  />
                </div>
                <span className="text-sm text-gray-500">
                  ({starFilter}+ stars)
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {starFilter > 0
                  ? starFilter === 1
                    ? "Showing jokes with 0-1 stars"
                    : `Showing jokes with ${starFilter} or more stars`
                  : "Select rating to filter jokes"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Current joke rating: {jokeDetails.stars || 0} ★
              </p>
            </div>

            {/* Top ranked section - as a filter */}
            <div className="mb-3 bg-gray-50 p-2 rounded-lg">
              <div className="flex items-center mb-1">
                <h2 className="text-blue-600 text-lg mr-3">
                  Show Top Ranked Only
                </h2>
                <ToggleSwitch
                  isOn={showTopRankedOnly}
                  onChange={handleToggleTopRankedFilter}
                />
              </div>
              <p className="text-xs text-gray-500">
                {showTopRankedOnly
                  ? "Showing only top ranked jokes"
                  : "Toggle to show only top ranked jokes"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Current joke is{" "}
                {jokeDetails.topRanked ? "top ranked" : "not top ranked"}
              </p>
            </div>

            {/* Punchline section - enhanced UI */}
            <div className="mb-3 bg-gray-50 p-2 rounded-lg">
              <div
                className={`flex items-center mb-1 ${
                  !jokeDetails.punchlineText ? "opacity-50" : ""
                }`}
              >
                <h2 className="text-blue-600 text-lg mr-3">Punchline</h2>
                <ToggleSwitch
                  isOn={showPunchline}
                  onChange={handleTogglePunchline}
                  disabled={!jokeDetails.punchlineText}
                />
              </div>

              {!jokeDetails.punchlineText && (
                <p className="text-xs text-gray-500 italic">
                  This joke doesn't have a punchline
                </p>
              )}

              {jokeDetails.punchlineText && !showPunchline && (
                <p className="text-xs text-gray-500">
                  Toggle to reveal the punchline
                </p>
              )}

              {showPunchline && jokeDetails.punchlineText && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-black">
                  <p className="text-sm font-medium">
                    {jokeDetails.punchlineText}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Middle section with model preview */}
        <div className="w-1/3 flex items-center justify-center border-l border-r border-gray-200 bg-white">
          <div className="w-full h-full flex flex-col items-center justify-center p-3">
            {roomUrl ? (
              <div className="w-full h-full flex flex-col">
                <iframe
                  src={roomUrl}
                  title="Simli Avatar Session"
                  className="w-full flex-grow rounded-lg shadow-md mb-4"
                  allow="camera; microphone; fullscreen"
                />
                <button
                  onClick={() => setRoomUrl(null)}
                  className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded mb-2"
                >
                  Close Session
                </button>
              </div>
            ) : (
              <>
                {/* SimliE2E Avatar container */}
                <div
                  ref={simliContainerRef}
                  className="max-w-full h-[calc(100vh-180px)] overflow-hidden flex items-center justify-center relative bg-gray-50 rounded-lg w-full"
                >
                  {isSimliLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-70 z-10">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  )}

                  {/* {!simliReady && !isSimliLoading && (
                    <div className="text-center p-4">
                      <p className="text-gray-500">Avatar loading...</p>
                    </div>
                  )} */}
                </div>

                {/* Avatar controls */}
                <div className="mt-4 flex flex-col w-full items-center justify-center">
                  {/* <button
                    className={`px-4 py-2 rounded-full mb-3 ${
                      isSimliPlaying
                        ? "bg-red-500 text-white"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    } 
                      transition-colors flex items-center justify-center ${
                        !simliReady ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    onClick={playJokeOnSimli}
                    disabled={!simliReady || isSimliPlaying}
                  >
                    {isSimliPlaying ? (
                      <>
                        <span className="mr-2">●</span>
                        Speaking...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">▶</span>
                        Play Joke
                      </>
                    )}
                  </button> */}

                  <button
                    className={`px-6 py-3 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors flex items-center justify-center ${
                      isStartingSession ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={startAvatarSession}
                    disabled={isStartingSession}
                  >
                    {isStartingSession ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white rounded-full mr-2"></div>
                        Starting Session...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">🚀</span>
                        Start Full Avatar Session
                      </>
                    )}
                  </button>

                  {error && (
                    <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
                      {error}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {/* Right panel with jokes list */}
        <div className="w-1/3 p-3 bg-white">
          <div className="bg-white rounded-lg shadow-md h-full flex flex-col">
            {/* Tabs for Random/Saved jokes */}
            <div className="flex">
              <button
                className={`flex-1 py-2 text-center font-medium ${
                  activeTab === "random"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
                onClick={() => handleTabChange("random")}
              >
                Random Jokes
              </button>
              <button
                className={`flex-1 py-2 text-center font-medium ${
                  activeTab === "saved"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
                onClick={() => handleTabChange("saved")}
              >
                Saved Jokes
              </button>
            </div>
            {/* Active filters display */}
            <div className="p-2">
              <div className="text-sm text-gray-600 mb-1">
                {activeTab === "random" ? (
                  <>
                    Showing {jokeDetails.familyFriendly ? "Kids" : "Adult"}{" "}
                    jokes
                    {starFilter > 0 && ` with ${starFilter}+ stars`}
                    {showTopRankedOnly && ", top ranked only"}
                  </>
                ) : (
                  <>Showing saved jokes</>
                )}{" "}
                ({jokeDetails.jokesList.length})
              </div>
            </div>
            {/* Jokes list */}
            <div className="p-2 flex-1 overflow-y-auto">
              {jokeDetails.jokesList.length === 0 ? (
                <div className="text-center p-4 text-gray-500">
                  {activeTab === "saved"
                    ? "No saved jokes yet. Try a different filter."
                    : "No jokes match your current filters."}
                </div>
              ) : (
                <div className="space-y-1">
                  {jokeDetails.jokesList.map((joke, index) => {
                    const isInactive =
                      index > 10 && index < jokeDetails.jokesList.length - 1;
                    return (
                      <div
                        key={joke._id || index}
                        className={`p-2 cursor-pointer transition-colors duration-200 rounded ${
                          currentJokeIndex === index
                            ? "bg-blue-100 text-black font-medium border border-blue-300"
                            : "bg-gray-100 text-black"
                        } ${isInactive ? "opacity-60" : "hover:bg-blue-50"}`}
                        onClick={() => handleJokeSelect(joke, index)}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-black flex-1">
                            {joke.title || joke.body}
                          </p>
                          {joke.topRanked && (
                            <span className="text-yellow-500 text-xs">★</span>
                          )}
                        </div>
                        {joke.stars > 0 && (
                          <div className="flex items-center mt-1">
                            <span className="text-yellow-500 text-xs mr-1">
                              ★
                            </span>
                            <span className="text-xs text-gray-500">
                              {joke.stars}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Footer buttons */}
      <div className="w-full flex justify-between p-9 bg-white border-t border-gray-200">
        <Link
          to="/joke"
          className="px-7 py-1  text-white font-medium rounded hover:bg-blue-600 transition-colors duration-200"
        ></Link>
        <div className="flex">
          <button
            className={`mx-2 rounded-full w-9 h-9 flex items-center justify-center text-white text-xl ${
              currentJokeIndex > 0
                ? "bg-blue-400 hover:bg-blue-500"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            onClick={handlePreviousJoke}
            disabled={currentJokeIndex === 0}
          >
            &larr;
          </button>
          <button
            className={`mx-2 rounded-full w-9 h-9 flex items-center justify-center text-white text-xl ${
              currentJokeIndex < jokeDetails.jokesList.length - 1
                ? "bg-blue-400 hover:bg-blue-500"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            onClick={handleNextJoke}
            disabled={currentJokeIndex >= jokeDetails.jokesList.length - 1}
          >
            &rarr;
          </button>
        </div>
        <button
          className="px-4 py-1 bg-blue-500 text-white font-medium rounded hover:bg-blue-600 transition-colors duration-200"
          onClick={handleSaveJoke}
        >
          Save
        </button>
      </div>

      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                Get More Jokes
              </h2>
              <button
                onClick={() => setShowPremiumModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-lg">
                  {user?.credits === 0 && user?.id
                    ? "You've used all your credits."
                    : `You've reached the limit of ${MAX_FREE_JOKES} free jokes.`}
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-blue-800 mb-2">
                  Why Subscribe?
                </h3>
                <ul className="text-blue-700 space-y-1">
                  <li className="flex items-center">
                    <svg
                      className="h-4 w-4 mr-2 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    Unlimited joke access
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-4 w-4 mr-2 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    Premium avatar features
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="h-4 w-4 mr-2 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    New jokes added regularly
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border border-gray-200 p-4 rounded-lg text-center hover:border-green-500 transition-colors cursor-pointer">
                <h3 className="font-bold text-lg mb-1">Basic Pack</h3>
                <p className="text-2xl font-semibold text-green-600 mb-2">
                  $4.99
                </p>
                <p className="text-gray-600 text-sm mb-3">50 joke credits</p>
                <p className="text-xs text-gray-500">
                  Perfect for casual users
                </p>
              </div>

              <div className="border-2 border-green-500 p-4 rounded-lg text-center relative bg-green-50">
                <span className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-1 transform translate-x-1 -translate-y-1/2 rounded">
                  Best Value
                </span>
                <h3 className="font-bold text-lg mb-1">Premium Pack</h3>
                <p className="text-2xl font-semibold text-green-600 mb-2">
                  $9.99
                </p>
                <p className="text-gray-600 text-sm mb-3">150 joke credits</p>
                <p className="text-xs text-gray-500">Most popular option!</p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPremiumModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={goToSubscriptionPage}
                className="px-6 py-2 bg-[#23c55e] text-white rounded-md hover:bg-[#1ea04d] transition-colors flex items-center"
              >
                <span className="mr-2">Get Credits</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Summary and Footer */}
      <div className="w-full mt-auto">
        {/* Billing Summary */}
        <BillingSummary />
      </div>
    </div>
  );
}
